<template>
  <div class="w-full max-w-6xl mx-auto space-y-6">
    <div class="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
      <div>
        <h1 class="text-2xl font-bold text-gray-900">Snapshot Configuration</h1>
        <p class="text-sm text-gray-600 mt-1">Control the public snapshot experience, purchasing rules, and matching options by university or ceremony.</p>
      </div>
      <div class="grid gap-3 w-full md:w-[34rem] md:grid-cols-2">
        <label class="block">
          <span class="text-sm font-medium text-gray-700">University</span>
          <select
            v-model="selectedUniversityId"
            class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
            @change="loadUniversity"
          >
            <option value="">Select a university</option>
            <option v-for="uni in universities" :key="uni.id" :value="uni.id">
              {{ uni.name }}
            </option>
          </select>
        </label>
        <label class="block">
          <span class="text-sm font-medium text-gray-700">Scope</span>
          <select
            v-model="scopeMode"
            class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
            @change="loadSelected"
          >
            <option value="university">University defaults</option>
            <option value="ceremony">Single ceremony override</option>
          </select>
        </label>
        <label v-if="scopeMode === 'ceremony'" class="block md:col-span-2">
          <span class="text-sm font-medium text-gray-700">Ceremony</span>
          <select
            v-model="selectedCeremonyId"
            class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
            @change="loadSelected"
          >
            <option value="">Select a ceremony</option>
            <option v-for="ceremony in ceremonies" :key="ceremony.id" :value="ceremony.id">
              {{ formatCeremony(ceremony) }}
            </option>
          </select>
        </label>
      </div>
    </div>

    <div v-if="error" class="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
      {{ error }}
    </div>
    <div v-if="success" class="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-700">
      {{ success }}
    </div>

    <div v-if="canEditScope" class="grid lg:grid-cols-3 gap-6">
      <form class="lg:col-span-2 bg-white border border-gray-200 rounded-lg p-5 space-y-6" @submit.prevent="saveConfig">
        <section class="space-y-3">
          <div>
            <h2 class="text-base font-semibold text-gray-900">Sales</h2>
            <p class="text-sm text-gray-500">Choose whether graduates pay for digital files and which product prices apply.</p>
          </div>
          <div class="grid md:grid-cols-2 gap-4">
            <label class="flex items-start gap-3 rounded-md border border-gray-200 px-3 py-3">
              <input v-model="config.isFreeDigital" type="checkbox" class="mt-1 h-4 w-4" />
              <span>
                <span class="block text-sm font-medium text-gray-800">Digital files are free</span>
                <span class="block text-xs text-gray-500">Orders with digital items skip Stripe checkout and send the collection email immediately.</span>
              </span>
            </label>
            <label class="block">
              <span class="text-sm font-medium text-gray-700">Gallery type</span>
              <select v-model="config.galleryMode" class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2">
                <option value="snapshot">Graduation snapshots</option>
                <option value="headshot">Professional headshots</option>
              </select>
            </label>
            <label class="block">
              <span class="text-sm font-medium text-gray-700">Price set</span>
              <select v-model="config.pricingTier" class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2">
                <option value="uk">UK products</option>
                <option value="aud">Australia products</option>
              </select>
            </label>
          </div>
        </section>

        <section class="space-y-4 border-t border-gray-100 pt-5">
          <div class="flex items-start justify-between gap-4">
            <div>
              <h2 class="text-base font-semibold text-gray-900">Products, Bundles And Delivery</h2>
              <p class="text-sm text-gray-500">Checkout products are loaded from the effective Snapshot configuration for this scope.</p>
            </div>
            <label class="flex items-center gap-2 text-sm font-medium text-gray-700">
              <input v-model="config.commerce.enabled" type="checkbox" class="h-4 w-4" />
              Enable checkout
            </label>
          </div>

          <div class="grid lg:grid-cols-3 gap-4">
            <div class="rounded-md border border-gray-200 p-4">
              <h3 class="text-sm font-semibold text-gray-900 mb-2">Digital</h3>
              <p v-for="product in config.commerce.digitalProducts" :key="product.code" class="text-sm text-gray-700">
                {{ product.label }}: {{ formatMoney(product.amount) }}
              </p>
              <p v-if="!config.commerce.digitalProducts.length" class="text-sm text-gray-500">No digital products configured.</p>
            </div>
            <div class="rounded-md border border-gray-200 p-4">
              <h3 class="text-sm font-semibold text-gray-900 mb-2">Prints</h3>
              <div v-for="product in config.commerce.printProducts" :key="product.code" class="mb-2 text-sm text-gray-700">
                <p>{{ product.label }}: {{ formatMoney(product.amount) }}</p>
                <p v-if="product.description" class="text-xs text-gray-500">{{ product.description }}</p>
              </div>
              <p v-if="!config.commerce.printProducts.length" class="text-sm text-gray-500">No print products configured.</p>
            </div>
            <div class="rounded-md border border-gray-200 p-4">
              <h3 class="text-sm font-semibold text-gray-900 mb-2">Delivery</h3>
              <p v-for="option in config.commerce.deliveryOptions" :key="option.code" class="text-sm text-gray-700">
                {{ option.label }}: {{ formatMoney(option.amount) }}
              </p>
              <p v-if="!config.commerce.deliveryOptions.length" class="text-sm text-gray-500">No delivery options configured.</p>
            </div>
            <div class="rounded-md border border-gray-200 p-4 lg:col-span-3">
              <h3 class="text-sm font-semibold text-gray-900 mb-2">Digital policy notes</h3>
              <p v-for="note in config.commerce.digitalPolicyNotes" :key="note" class="text-sm text-gray-700">
                {{ note }}
              </p>
            </div>
            <div class="rounded-md border border-gray-200 p-4 lg:col-span-3">
              <h3 class="text-sm font-semibold text-gray-900 mb-2">Print policy notes</h3>
              <p v-for="note in config.commerce.customerPolicyNotes" :key="note" class="text-sm text-gray-700">
                {{ note }}
              </p>
              <p v-if="config.commerce.termsUrl" class="mt-2 text-sm text-gray-700">
                Terms:
                <a :href="config.commerce.termsUrl" target="_blank" rel="noopener" class="font-medium text-blue-700 underline">
                  Terms and conditions
                </a>
              </p>
            </div>
          </div>
        </section>

        <section class="space-y-3 border-t border-gray-100 pt-5">
          <div>
            <h2 class="text-base font-semibold text-gray-900">Public Snapshot Page</h2>
            <p class="text-sm text-gray-500">These settings change what graduates see while finding and selecting photos.</p>
          </div>
          <div class="grid md:grid-cols-2 gap-4">
            <label v-if="scopeMode === 'ceremony'" class="flex items-start gap-3 rounded-md border border-gray-200 px-3 py-3">
              <input v-model="config.watermarkEnabled" type="checkbox" class="mt-1 h-4 w-4" />
              <span>
                <span class="block text-sm font-medium text-gray-800">Watermark preview images</span>
                <span class="block text-xs text-gray-500">Turn off for this gallery only. Cached previews may take a few minutes to refresh.</span>
              </span>
            </label>
            <label v-for="field in publicFields" :key="field.key" class="flex items-start gap-3 rounded-md border border-gray-200 px-3 py-3">
              <input v-model="config[field.key]" type="checkbox" class="mt-1 h-4 w-4" />
              <span>
                <span class="block text-sm font-medium text-gray-800">{{ field.label }}</span>
                <span class="block text-xs text-gray-500">{{ field.help }}</span>
              </span>
            </label>
            <label class="block">
              <span class="text-sm font-medium text-gray-700">Selfie prompt</span>
              <select v-model="config.faceSearchRecommended" class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2">
                <option :value="true">Recommend selfie search</option>
                <option :value="false">Make selfie search optional</option>
              </select>
            </label>
            <label class="block">
              <span class="text-sm font-medium text-gray-700">Print options</span>
              <select v-model="config.printsEnabled" class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2">
                <option :value="true">Show print options</option>
                <option :value="false">Hide print options</option>
              </select>
            </label>
            <label class="block">
              <span class="text-sm font-medium text-gray-700">Remember details for</span>
              <div class="mt-1 flex items-center gap-2">
                <input v-model.number="config.rememberMeTtlDays" type="number" min="1" max="365" class="w-28 rounded-md border border-gray-300 px-3 py-2" />
                <span class="text-sm text-gray-500">days</span>
              </div>
            </label>
            <label class="block">
              <span class="text-sm font-medium text-gray-700">Show photos as new for</span>
              <div class="mt-1 flex items-center gap-2">
                <input v-model.number="config.newImagesTtlDays" type="number" min="1" max="365" class="w-28 rounded-md border border-gray-300 px-3 py-2" />
                <span class="text-sm text-gray-500">days</span>
              </div>
            </label>
          </div>
        </section>

        <details class="border-t border-gray-100 pt-5">
          <summary class="cursor-pointer text-base font-semibold text-gray-900">Consent and selfie matching settings</summary>
          <div class="mt-3 grid md:grid-cols-2 gap-4">
            <label class="block md:col-span-2">
              <span class="text-sm font-medium text-gray-700">Consent wording</span>
              <select v-model="config.consentMode" class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2">
                <option value="none">No consent wording</option>
                <option value="ucd_daro">UCD DARO headshot wording</option>
                <option value="custom">Custom consent wording</option>
              </select>
            </label>
            <label v-for="field in consentFields" :key="field.key" class="flex items-start gap-3 rounded-md border border-gray-200 px-3 py-3">
              <input v-model="config[field.key]" type="checkbox" class="mt-1 h-4 w-4" />
              <span>
                <span class="block text-sm font-medium text-gray-800">{{ field.label }}</span>
                <span class="block text-xs text-gray-500">{{ field.help }}</span>
              </span>
            </label>
            <label class="block">
              <span class="text-sm font-medium text-gray-700">Consent form URL</span>
              <input v-model.trim="config.consentUrl" class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2" />
            </label>
            <label class="block">
              <span class="text-sm font-medium text-gray-700">Consent notification email</span>
              <input v-model.trim="config.consentEmail" class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2" />
            </label>
            <label class="block">
              <span class="text-sm font-medium text-gray-700">Consent storage reference</span>
              <input v-model.trim="config.consentStorageKey" class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2" />
            </label>
            <label v-if="config.consentMode === 'custom'" class="block md:col-span-2">
              <span class="text-sm font-medium text-gray-700">Custom consent title</span>
              <input v-model.trim="config.consentTitle" class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2" />
            </label>
            <label v-if="config.consentMode === 'custom'" class="block md:col-span-2">
              <span class="text-sm font-medium text-gray-700">Custom consent body</span>
              <textarea v-model.trim="config.consentBody" rows="5" class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"></textarea>
            </label>
            <label v-if="config.consentMode === 'custom'" class="block md:col-span-2">
              <span class="text-sm font-medium text-gray-700">Custom consent checkbox text</span>
              <input v-model.trim="config.consentCheckboxText" class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2" />
            </label>
            <label class="block">
              <span class="text-sm font-medium text-gray-700">Selfie consent version</span>
              <input v-model.trim="config.saveSelfieConsentVersion" class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2" />
            </label>
            <label class="block">
              <span class="text-sm font-medium text-gray-700">Selfie privacy policy URL</span>
              <input v-model.trim="config.saveSelfieConsentPolicyUrl" class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2" />
            </label>
            <label class="block">
              <span class="text-sm font-medium text-gray-700">Organisation shown in consent text</span>
              <input v-model.trim="config.saveSelfieConsentOrgName" class="mt-1 w-full rounded-md border border-gray-300 px-3 py-2" />
            </label>
          </div>
        </details>

        <div class="flex items-center gap-3">
          <button
            type="submit"
            class="inline-flex items-center px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            :disabled="saving || loading"
          >
            {{ saving ? 'Saving...' : 'Save configuration' }}
          </button>
          <span v-if="updatedAt" class="text-xs text-gray-500">Last saved {{ formatDate(updatedAt) }}</span>
        </div>
      </form>

      <div class="space-y-4">
        <div class="bg-white border border-gray-200 rounded-lg p-4">
          <h2 class="font-semibold text-gray-900 mb-1">Snapshot Orders</h2>
          <p class="text-xs text-gray-500 mb-3">Completed orders and free claims for this scope.</p>
          <dl class="grid grid-cols-2 gap-3 text-sm">
            <div v-for="card in summaryCards" :key="card.label" class="rounded-md border border-gray-200 p-3">
              <dt class="text-gray-500">{{ card.label }}</dt>
              <dd class="text-2xl font-semibold text-gray-900">{{ card.value }}</dd>
            </div>
          </dl>
          <p v-if="summaryError" class="mt-3 text-xs text-amber-700">
            {{ summaryError }}
          </p>
          <router-link
            class="mt-3 inline-flex items-center text-sm font-medium text-blue-700 hover:text-blue-900"
            :to="snapshotOrdersPath"
          >
            Open order details
          </router-link>
        </div>
        <div class="bg-white border border-gray-200 rounded-lg p-4">
          <h2 class="font-semibold text-gray-900 mb-2">Current Scope</h2>
          <p class="text-sm text-gray-700">{{ scopeTitle }}</p>
          <p class="text-xs text-gray-500 mt-1">{{ scopeSubtitle }}</p>
          <p v-if="scopeMode === 'ceremony'" class="mt-2 text-xs text-gray-500">
            Ceremony settings inherit the university defaults and override only this ceremony.
          </p>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import axios from 'axios'
import { trackEvent } from '../../Analytics.vue'

const DEFAULT_CONFIG = {
  requiresConsent: false,
  consentUrl: '',
  consentEmail: '',
  consentStorageKey: '',
  consentTitle: '',
  consentBody: '',
  consentCheckboxText: '',
  isFreeDigital: false,
  isHeadshot: false,
  galleryMode: 'snapshot',
  printsEnabled: true,
  watermarkEnabled: true,
  faceSearchRecommended: true,
  consentMode: 'none',
  ceremonyLabelOnly: false,
  eventTerminology: false,
  pricingTier: 'uk',
  rememberMeEnabled: true,
  saveSelfieEnabled: false,
  rememberMeTtlDays: 30,
  newImagesTtlDays: 7,
  saveSelfieConsentVersion: 'v1',
  saveSelfieConsentPolicyUrl: '/privacy-policy',
  saveSelfieConsentOrgName: 'MyGraduationClip',
  commerce: {
    enabled: false,
    currency: 'gbp',
    digitalProducts: [],
    printProducts: [],
    deliveryOptions: [],
    termsUrl: '',
    digitalPolicyNotes: [],
    customerPolicyNotes: [],
  },
}

export default {
  name: 'SnapshotConfiguration',
  data() {
    return {
      universities: [],
      selectedUniversityId: '',
      ceremonies: [],
      selectedCeremonyId: '',
      scopeMode: 'university',
      config: { ...DEFAULT_CONFIG },
      summary: {},
      loading: false,
      saving: false,
      error: '',
      success: '',
      summaryError: '',
      updatedAt: null,
      publicFields: [
        { key: 'ceremonyLabelOnly', label: 'Use ceremony labels only', help: 'Shows the ceremony label instead of full ceremony date/time copy.' },
        { key: 'eventTerminology', label: 'Use event wording', help: 'Shows event wording instead of graduation or ceremony wording on the public Snapshot page.' },
        { key: 'rememberMeEnabled', label: 'Remember graduate details', help: 'Lets the browser remember name, email, and selfie state for return visits.' },
      ],
      consentFields: [
        { key: 'requiresConsent', label: 'Require consent before search', help: 'Ask graduates to accept consent terms before they can search for photos.' },
        { key: 'saveSelfieEnabled', label: 'Allow saved selfie matching', help: 'Let graduates save a selfie subscription for later photo match notifications.' },
      ],
    }
  },
  computed: {
    selectedUniversity() {
      return this.universities.find((u) => u.id === this.selectedUniversityId) || null
    },
    selectedUniversityName() {
      return this.selectedUniversity ? this.selectedUniversity.name : ''
    },
    selectedUniversitySlug() {
      return this.selectedUniversity ? this.selectedUniversity.slug : ''
    },
    selectedCeremony() {
      return this.ceremonies.find((c) => c.id === this.selectedCeremonyId) || null
    },
    canEditScope() {
      if (!this.selectedUniversityId) return false
      return this.scopeMode !== 'ceremony' || Boolean(this.selectedCeremonyId)
    },
    scopeTitle() {
      if (this.scopeMode === 'ceremony') return this.formatCeremony(this.selectedCeremony)
      return this.selectedUniversityName
    },
    scopeSubtitle() {
      if (this.scopeMode === 'ceremony') return `${this.selectedUniversityName} · ${this.selectedCeremonyId}`
      return this.selectedUniversitySlug
    },
    configEndpoint() {
      const base = `${this.apiBase()}/admin/snapshot/config/universities/${this.selectedUniversityId}`
      if (this.scopeMode === 'ceremony') return `${base}/ceremonies/${this.selectedCeremonyId}`
      return base
    },
    ordersEndpoint() {
      const base = `${this.apiBase()}/admin/snapshot/orders/universities/${this.selectedUniversityId}`
      if (this.scopeMode === 'ceremony') return `${base}/ceremonies/${this.selectedCeremonyId}`
      return base
    },
    snapshotOrdersPath() {
      const query = {}
      if (this.selectedUniversityId) query.university_id = this.selectedUniversityId
      if (this.scopeMode === 'ceremony' && this.selectedCeremonyId) query.ceremony_id = this.selectedCeremonyId
      return { path: '/admin/snapshot-orders', query }
    },
    summaryCards() {
      return [
        { label: 'Completed orders', value: this.summary.completed_orders || 0 },
        { label: 'Downloaded', value: this.summary.downloaded_orders || 0 },
        { label: 'Print items', value: this.summary.print_items || 0 },
        { label: 'Digital items', value: this.summary.digital_items || 0 },
      ]
    },
  },
  created() {
    this.loadUniversities()
    trackEvent(this.$route.path, { label: 'SNAPSHOT_CONFIG_OPENED' })
  },
  methods: {
    apiBase() {
      return process.env.VUE_APP_SNAPSHOT_API_BASE || ''
    },
    async loadUniversities() {
      try {
        const r = await axios.get('/universities')
        this.universities = r.data || []
        if (this.universities.length && !this.selectedUniversityId) {
          this.selectedUniversityId = this.universities[0].id
          await this.loadUniversity()
        }
      } catch (e) {
        this.error = 'Unable to load universities.'
      }
    },
    async loadUniversity() {
      this.selectedCeremonyId = ''
      this.ceremonies = []
      if (!this.selectedUniversityId) return
      this.error = ''
      try {
        const r = await axios.get(`/universities/${this.selectedUniversityId}/ceremonies`)
        this.ceremonies = r.data || []
      } catch (e) {
        this.error = 'Unable to load ceremonies.'
      }
      await this.loadSelected()
    },
    async loadSelected() {
      if (!this.canEditScope) return
      this.error = ''
      this.success = ''
      this.summaryError = ''
      this.loading = true
      try {
        const configResponse = await axios.get(this.configEndpoint)
        this.config = this.mergeConfig(configResponse.data.config || {})
        this.normalizeCompatibilityConfig()
        this.updatedAt = configResponse.data.updated_at || null
        await this.loadOrderSummary()
      } catch (e) {
        this.error = 'Unable to load snapshot configuration.'
      } finally {
        this.loading = false
      }
    },
    async loadOrderSummary() {
      if (!this.canEditScope) return
      this.summaryError = ''
      try {
        const r = await axios.get(this.ordersEndpoint)
        this.summary = r.data.summary || {}
      } catch (e) {
        this.summary = {}
        this.summaryError = 'Order summary unavailable.'
      }
    },
    async saveConfig() {
      if (!this.canEditScope) return
      this.saving = true
      this.error = ''
      this.success = ''
      this.summaryError = ''
      try {
        this.normalizeCompatibilityConfig()
        const r = await axios.put(
          this.configEndpoint,
          { config: this.config }
        )
        this.config = this.mergeConfig(r.data.config || {})
        this.normalizeCompatibilityConfig()
        this.success = 'Snapshot configuration saved.'
        trackEvent(this.$route.path, {
          label: 'SNAPSHOT_CONFIG_SAVED',
          university_id: this.selectedUniversityId,
          ceremony_id: this.scopeMode === 'ceremony' ? this.selectedCeremonyId : null,
          scope: this.scopeMode,
        })
      } catch (e) {
        this.error = 'Unable to save snapshot configuration.'
      } finally {
        this.saving = false
      }
      if (!this.error) await this.loadOrderSummary()
    },
    formatDate(value) {
      if (!value) return ''
      try { return new Date(value).toLocaleString() } catch (e) { return String(value) }
    },
    formatCeremony(ceremony) {
      if (!ceremony) return ''
      if (ceremony.label) return ceremony.label
      const value = ceremony.datetime || ceremony.ceremony_datetime || ceremony.id || ''
      try { return new Date(value).toLocaleString() } catch (e) { return String(value) }
    },
    normalizeCompatibilityConfig() {
      this.config = this.mergeConfig(this.config)
      if (!this.config.galleryMode || this.config.galleryMode === 'snapshot') {
        this.config.galleryMode = this.config.isHeadshot ? 'headshot' : 'snapshot'
      }
      this.config.isHeadshot = this.config.galleryMode === 'headshot'
    },
    mergeConfig(raw) {
      const commerce = {
        ...DEFAULT_CONFIG.commerce,
        ...((raw || {}).commerce || {}),
      }
      commerce.deliveryOptions = Array.isArray(commerce.deliveryOptions) ? commerce.deliveryOptions : DEFAULT_CONFIG.commerce.deliveryOptions
      commerce.deliveryOptions = commerce.deliveryOptions.map((option) => ({
        ...option,
        allowedCountries: Array.isArray(option.allowedCountries) ? option.allowedCountries : (option.requiresAddress || option.type === 'shipping' ? ['GB'] : []),
      }))
      commerce.digitalProducts = Array.isArray(commerce.digitalProducts) ? commerce.digitalProducts : DEFAULT_CONFIG.commerce.digitalProducts
      commerce.printProducts = Array.isArray(commerce.printProducts) ? commerce.printProducts : DEFAULT_CONFIG.commerce.printProducts
      commerce.digitalPolicyNotes = Array.isArray(commerce.digitalPolicyNotes) ? commerce.digitalPolicyNotes : DEFAULT_CONFIG.commerce.digitalPolicyNotes
      commerce.customerPolicyNotes = Array.isArray(commerce.customerPolicyNotes) ? commerce.customerPolicyNotes : DEFAULT_CONFIG.commerce.customerPolicyNotes
      return { ...DEFAULT_CONFIG, ...(raw || {}), commerce }
    },
    formatMoney(value) {
      return `£${(Number(value || 0) / 100).toFixed(2)}`
    },
  },
}
</script>
